// Player survival state (ARCH §3.4/§3.6, PLR-1/5). PURE (ENG-1).
//
// The sim-side player: game mode, health/hunger/air/xp, the inventory, and spawn
// point — everything persisted under the `player` store (§3.6) and (v1.1)
// mirrored to the server. Position/orientation live with the physics body on the
// host; this object owns the survival numbers and the item container. Hunger and
// the full damage pipeline tick in M4.3 (hunger.ts / combat.ts); the mutators
// here are the clamped primitives those systems call.
import { SURVIVAL } from '../config';
import { PlayerInventory, type ArmorSlot, type ItemStack } from './inventory';
import { type GameMode, modeCaps } from './modes';
import type { ActiveEffect } from './effects';

/** Levelling costs (PLR-5): points to advance from level `n` to `n+1`. */
export function xpForLevel(n: number): number {
  if (n < 16) return 2 * n + 7;
  if (n < 31) return 5 * n - 38;
  return 9 * n - 158;
}

export interface PlayerSave {
  gameMode: GameMode;
  health: number;
  hunger: number;
  saturation: number;
  exhaustion: number;
  air: number;
  xpLevel: number;
  xpProgress: number;
  xpTotal: number;
  spawn: { x: number; y: number; z: number } | null;
  timeSinceRest: number;
  inventory: (ItemStack | null)[];
  selected: number;
}

export class Player {
  readonly inventory: PlayerInventory;

  gameMode: GameMode;
  health: number;
  absorption = 0;
  hunger: number;
  saturation: number;
  /** Accrues from activity; at ≥4 drains one saturation/hunger (PLR-4, M4.3). */
  exhaustion = 0;
  air: number;
  xpLevel = 0;
  xpProgress = 0;
  xpTotal = 0;
  spawn: { x: number; y: number; z: number } | null = null;

  // Transient survival timers (PLR-4/9; not persisted — reset on load).
  regenTimer = 0;
  starveTimer = 0;
  drownTimer = 0;
  /** Remaining on-fire ticks (lava / ghast fireball, M7.2) + its damage cadence. */
  fireTicks = 0;
  fireDamageTimer = 0;
  lavaDamageTimer = 0;
  /** Highest Y reached since leaving the ground (fall-distance accounting). */
  fallPeak = 0;
  /** Ticks since the player last slept (PLR-10 insomnia → phantoms, M13). */
  timeSinceRest = 0;
  /** Active status effects (PLR-8; M12.2). Transient — not persisted. */
  readonly effects: ActiveEffect[] = [];

  constructor(
    maxStack: (item: number) => number,
    gameMode: GameMode = 'survival',
    armorSlotOf: (item: number) => ArmorSlot | null = () => null,
  ) {
    this.inventory = new PlayerInventory(maxStack, armorSlotOf);
    this.gameMode = gameMode;
    this.health = SURVIVAL.MAX_HEALTH;
    this.hunger = SURVIVAL.START_HUNGER;
    this.saturation = SURVIVAL.START_SATURATION;
    this.air = SURVIVAL.MAX_AIR;
  }

  get caps() {
    return modeCaps(this.gameMode);
  }

  get maxHealth(): number {
    return SURVIVAL.MAX_HEALTH;
  }

  get dead(): boolean {
    return this.health <= 0;
  }

  /** Apply raw damage (post-mitigation) to absorption then health. No-op when
   *  the mode is invulnerable. Returns the health actually lost. */
  applyDamage(amount: number): number {
    if (amount <= 0 || this.caps.invulnerable) return 0;
    let dmg = amount;
    if (this.absorption > 0) {
      const soaked = Math.min(this.absorption, dmg);
      this.absorption -= soaked;
      dmg -= soaked;
    }
    const before = this.health;
    this.health = Math.max(0, this.health - dmg);
    return before - this.health;
  }

  heal(amount: number): void {
    if (amount <= 0) return;
    this.health = Math.min(this.maxHealth, this.health + amount);
  }

  /** PLR-4: activity exhaustion; the drain step lives in hunger.ts (M4.3). */
  addExhaustion(amount: number): void {
    this.exhaustion += amount;
  }

  /** Add (or, negative, remove) experience, rolling levels per PLR-5. */
  addExperience(amount: number): void {
    this.xpTotal = Math.max(0, this.xpTotal + Math.max(0, amount));
    let points = Math.round(this.xpProgress * xpForLevel(this.xpLevel)) + amount;
    while (points >= xpForLevel(this.xpLevel)) {
      points -= xpForLevel(this.xpLevel);
      this.xpLevel++;
    }
    while (points < 0 && this.xpLevel > 0) {
      this.xpLevel--;
      points += xpForLevel(this.xpLevel);
    }
    if (points < 0) points = 0;
    this.xpProgress = points / xpForLevel(this.xpLevel);
  }

  /** Spend `n` experience levels (enchanting/anvil, PLR-6). Returns false if the
   *  player can't afford it; otherwise resets progress and recomputes total xp. */
  takeLevels(n: number): boolean {
    if (n <= 0) return true;
    if (this.xpLevel < n) return false;
    this.xpLevel -= n;
    this.xpProgress = 0;
    let total = 0;
    for (let i = 0; i < this.xpLevel; i++) total += xpForLevel(i);
    this.xpTotal = total;
    return true;
  }

  /** Reset to a fresh spawn (respawn): full health/hunger, cleared xp progress. */
  resetForRespawn(): void {
    this.health = this.maxHealth;
    this.absorption = 0;
    this.hunger = SURVIVAL.START_HUNGER;
    this.saturation = SURVIVAL.START_SATURATION;
    this.exhaustion = 0;
    this.air = SURVIVAL.MAX_AIR;
    this.fireTicks = 0;
    this.fireDamageTimer = 0;
    this.lavaDamageTimer = 0;
  }

  toJSON(): PlayerSave {
    return {
      gameMode: this.gameMode,
      health: this.health,
      hunger: this.hunger,
      saturation: this.saturation,
      exhaustion: this.exhaustion,
      air: this.air,
      xpLevel: this.xpLevel,
      xpProgress: this.xpProgress,
      xpTotal: this.xpTotal,
      spawn: this.spawn,
      timeSinceRest: this.timeSinceRest,
      inventory: this.inventory.toJSON(),
      selected: this.inventory.selected,
    };
  }

  loadJSON(save: Partial<PlayerSave> | undefined): void {
    if (!save) return;
    if (save.gameMode) this.gameMode = save.gameMode;
    if (typeof save.health === 'number') this.health = save.health;
    if (typeof save.hunger === 'number') this.hunger = save.hunger;
    if (typeof save.saturation === 'number') this.saturation = save.saturation;
    if (typeof save.exhaustion === 'number') this.exhaustion = save.exhaustion;
    if (typeof save.air === 'number') this.air = save.air;
    if (typeof save.xpLevel === 'number') this.xpLevel = save.xpLevel;
    if (typeof save.xpProgress === 'number') this.xpProgress = save.xpProgress;
    if (typeof save.xpTotal === 'number') this.xpTotal = save.xpTotal;
    if (save.spawn !== undefined) this.spawn = save.spawn;
    if (typeof save.timeSinceRest === 'number') this.timeSinceRest = save.timeSinceRest;
    this.inventory.loadJSON(save.inventory);
    if (typeof save.selected === 'number') this.inventory.selected = save.selected;
  }
}
